import {
	IListAttrs,
	IListColumnConfig,
	IListOperations,
} from '@datachain/ui-sdk/common';
import { immerable, produce } from 'immer';
import { Map, OrderedSet, Set } from 'immutable';

import { ExpositionConsumerMappingEntity } from '../../domain/exposition-consumer-mapping.entity';

export interface IConsumerMappingListExtra
	extends IListColumnConfig<ExpositionConsumerMappingEntity> {
	isFixed?: boolean;
	isSelectable?: boolean;
	isSearchable?: boolean;
	isInGroup?: boolean;
	isVisible?: boolean;
	canBeHidden?: boolean;
	canBeReordered?: boolean;
	canBeChosen?: boolean;
	format?: string;
	visibleIndex?: number;
	width?: string | number;
}

interface IConsumerMappingListEntity
	extends IListAttrs<ExpositionConsumerMappingEntity> {
	availableColumns: Map<string, IConsumerMappingListExtra>;
	hasNoMapping: boolean;
}

const entityDefaults: IConsumerMappingListEntity = {
	elements: Map(),
	availableColumns: Map(),
	hasNoMapping: false,
};

export class ExpositionConsumerMappingListEntity
	implements
		IConsumerMappingListEntity,
		IListOperations<
			ExpositionConsumerMappingListEntity,
			ExpositionConsumerMappingEntity
		>
{
	private [immerable] = true;
	public elements = entityDefaults.elements;
	public availableColumns = entityDefaults.availableColumns;
	public hasNoMapping = entityDefaults.hasNoMapping;

	public static build(
		availableCols: Array<IConsumerMappingListExtra>
	): ExpositionConsumerMappingListEntity {
		const inst = new ExpositionConsumerMappingListEntity();
		const cols = availableCols.reduce((acc, curr) => {
			acc.push([curr.field, curr]);
			return acc;
		}, new Array<[string, IConsumerMappingListExtra]>());
		inst.availableColumns = Map(cols);
		return inst;
	}

	private constructor() {}

	public getAvailableColumns(): Map<
		string,
		IListColumnConfig<ExpositionConsumerMappingEntity>
	> {
		return this.availableColumns;
	}
	public setElements(
		elems: Map<string, ExpositionConsumerMappingEntity>
	): ExpositionConsumerMappingListEntity {
		const list = produce(this, (draft: ExpositionConsumerMappingListEntity) => {
			draft.elements = elems;
		});
		return list.checkIfNoMapping();
	}

	public checkIfNoMapping(): ExpositionConsumerMappingListEntity {
		return produce(this, (draft: ExpositionConsumerMappingListEntity) => {
			draft.hasNoMapping = this.elements.some(
				(item: ExpositionConsumerMappingEntity) =>
					item.targetConsumers.size === 0
			);
		});
	}

	public extractUniqTargets(...startValues: Array<string>): Set<string> {
		const allTargets = this.elements
			.reduce((acc, consumerToMap) => {
				if (!consumerToMap.targetConsumers) {
					return acc;
				}

				return Set([
					...acc.toArray(),
					...consumerToMap.targetConsumers
						.toList()
						.toArray()
						.map((l) => l.name),
				]);
			}, Set<string>())
			.sort((a, b) => a.localeCompare(b));

		return OrderedSet<string>([...startValues, ...allTargets]);
	}
}
